import { COMPANY_INTENT_QUESTION_COUNT } from "./companyIntent.types";

export const INTENT_QUESTIONNAIRE_VERSION = 9;

export type IntentQuestionSection = "who_we_are" | "looking_for";

export type IntentQuestionSelectionMode = "single" | "multiple";

export type IntentQuestionFollowUpDefinition = {
  enabled: true;
  optional: true;
  type: "textarea";
  prompt: string;
  maxLength: number;
};

export type IntentAnswerGroupDefinition = {
  id: string;
  prompt: string;
  selectionMode: IntentQuestionSelectionMode;
  inputType?: "catalog_geography";
};

export type IntentQuestionDefinition = {
  id: string;
  section: IntentQuestionSection;
  title: string;
  decisionContext: string;
  userPromptGuide: string;
  selectionMode: IntentQuestionSelectionMode;
  /** When set with selectionMode "multiple", caps how many options may be selected (e.g. Q11 = 2). */
  maxSelections?: number;
  decisionSignals: readonly string[];
  followUp?: IntentQuestionFollowUpDefinition;
  answerGroupDefinitions?: readonly IntentAnswerGroupDefinition[];
};

export const CURRENT_INTENT_QUESTION_IDS = [
  "q1_company_scale_stability",
  "q2_customer_traction_validation",
  "q3_customer_profile_segment",
  "q4_offering_maturity_proof",
  "q5_market_presence_expansion",
  "q6_ideal_partner_profile",
  "q7_roles_responsibilities_support",
  "q8_commercial_opportunity_economics",
  "q9_credibility_evidence",
  "q10_collaboration_intent_outcome",
  "q11_partner_type_sought",
  "q12_capabilities_needed",
  "q13_target_markets_sought",
  "q14_timeline_readiness",
  "q15_deal_structure_preferences",
] as const;

export type CurrentIntentQuestionId = (typeof CURRENT_INTENT_QUESTION_IDS)[number];

export const WHO_WE_ARE_DIMENSIONS = CURRENT_INTENT_QUESTION_IDS.slice(0, 10);
export const LOOKING_FOR_DIMENSIONS = CURRENT_INTENT_QUESTION_IDS.slice(10, 15);

const _assertIntentQuestionIdCount: (typeof CURRENT_INTENT_QUESTION_IDS)["length"] =
  COMPANY_INTENT_QUESTION_COUNT;

export const Q11_PARTNER_TYPE_ID = "q11_partner_type_sought";
export const Q15_PRIORITY_MARKET_ID = "q15_deal_structure_preferences";

export const Q15_GROUP_PRIORITY_COUNTRIES = "priority_countries";

export const ANSWER_GROUP_QUESTION_IDS = [Q15_PRIORITY_MARKET_ID] as const;

export const Q15_ANSWER_GROUP_DEFINITIONS: readonly IntentAnswerGroupDefinition[] = [
  {
    id: Q15_GROUP_PRIORITY_COUNTRIES,
    prompt: "Select the specific country or countries within your priority region",
    selectionMode: "multiple",
    inputType: "catalog_geography",
  },
];

export const SECTION_LABELS: Record<IntentQuestionSection, string> = {
  who_we_are: "About Your Company",
  looking_for: "What Are You Looking For?",
};

export const FOLLOW_UP_QUESTION_IDS = [Q15_PRIORITY_MARKET_ID] as const;

export const SINGLE_SELECT_QUESTION_IDS = [
  "q1_company_scale_stability",
  "q2_customer_traction_validation",
  "q3_customer_profile_segment",
  "q6_ideal_partner_profile",
  "q9_credibility_evidence",
  "q15_deal_structure_preferences",
] as const satisfies readonly CurrentIntentQuestionId[];

const STANDARD_FOLLOW_UP: IntentQuestionFollowUpDefinition = {
  enabled: true,
  optional: true,
  type: "textarea",
  prompt: "Other / Add context (optional)",
  maxLength: 300,
};

export const QUESTIONNAIRE_FOLLOW_UP = STANDARD_FOLLOW_UP;

export const INTENT_QUESTION_DEFINITIONS: readonly IntentQuestionDefinition[] = [
  {
    id: "q1_company_scale_stability",
    section: "who_we_are",
    title: "Commercial Experience",
    decisionContext:
      "Partners need to understand how long the company has been commercially active in its core offering.",
    userPromptGuide:
      "How long has your company been commercially manufacturing and deploying its core solutions?",
    selectionMode: "single",
    decisionSignals: ["commercial_experience", "market_tenure"],
  },
  {
    id: "q2_customer_traction_validation",
    section: "who_we_are",
    title: "Delivery Track Record",
    decisionContext: "Partners need evidence of completed commercial project volume.",
    userPromptGuide: "How many commercial projects has your company completed?",
    selectionMode: "single",
    decisionSignals: ["delivery_track_record", "project_count"],
  },
  {
    id: "q3_customer_profile_segment",
    section: "who_we_are",
    title: "Demonstrated Scale",
    decisionContext: "Partners need to understand the largest scale the company has successfully delivered.",
    userPromptGuide:
      "What is the largest project (by physical volume or capacity, not monetary value) your company has successfully delivered under a single contract?",
    selectionMode: "single",
    decisionSignals: ["demonstrated_scale", "largest_project"],
  },
  {
    id: "q4_offering_maturity_proof",
    section: "who_we_are",
    title: "Offering Depth",
    decisionContext: "Partners need clarity on the depth and breadth of the company's commercial offering.",
    userPromptGuide: "In which offering categories has your company commercially deployed solutions?",
    selectionMode: "multiple",
    decisionSignals: ["offering_depth", "product_range"],
  },
  {
    id: "q5_market_presence_expansion",
    section: "who_we_are",
    title: "Core Capabilities",
    decisionContext: "Partners need to know which core capabilities are available in commercially deployed solutions.",
    userPromptGuide: "Which core capabilities are currently available in your commercially deployed solutions?",
    selectionMode: "multiple",
    decisionSignals: ["core_capabilities", "technical_capability"],
  },
  {
    id: "q6_ideal_partner_profile",
    section: "who_we_are",
    title: "Customisation and Integration",
    decisionContext:
      "Partners need to understand the highest level of customisation and integration the company regularly delivers.",
    userPromptGuide: "What is the highest level of project customisation your company regularly delivers?",
    selectionMode: "single",
    decisionSignals: ["customisation_depth", "integration_level"],
  },
  {
    id: "q7_roles_responsibilities_support",
    section: "who_we_are",
    title: "Proven Industries",
    decisionContext: "Partners need to know in which industries the company's solutions operate commercially.",
    userPromptGuide: "In which industries are your solutions already operating commercially?",
    selectionMode: "multiple",
    decisionSignals: ["proven_industries", "sector_experience"],
  },
  {
    id: "q8_commercial_opportunity_economics",
    section: "who_we_are",
    title: "Evidence and Outcomes",
    decisionContext: "Partners need evidence the company can demonstrate successful project delivery.",
    userPromptGuide: "What evidence can your company provide to demonstrate successful project delivery?",
    selectionMode: "multiple",
    decisionSignals: ["evidence", "measurable_outcomes"],
  },
  {
    id: "q9_credibility_evidence",
    section: "who_we_are",
    title: "International Delivery Capacity",
    decisionContext:
      "Partners need to understand how many international projects the company can support concurrently.",
    userPromptGuide: "How many international projects can your company technically support at the same time?",
    selectionMode: "single",
    decisionSignals: ["international_capacity", "concurrent_projects"],
  },
  {
    id: "q10_collaboration_intent_outcome",
    section: "who_we_are",
    title: "Quality, Compliance and Reliability",
    decisionContext:
      "Partners need clarity on quality, testing and compliance capabilities for international projects.",
    userPromptGuide:
      "Which quality and compliance capabilities can your company provide for international projects?",
    selectionMode: "multiple",
    decisionSignals: ["quality_compliance", "certification_readiness"],
  },
  {
    id: Q11_PARTNER_TYPE_ID,
    section: "looking_for",
    title: "Preferred Partner Type",
    decisionContext: "Company states what type of partner organization it is currently seeking.",
    userPromptGuide: "What type of partner are you currently seeking?",
    selectionMode: "multiple",
    maxSelections: 2,
    decisionSignals: ["partner_archetype", "partner_type"],
  },
  {
    id: "q12_capabilities_needed",
    section: "looking_for",
    title: "Expected Partner Responsibilities",
    decisionContext: "Company states which responsibilities the partner should undertake.",
    userPromptGuide: "Which responsibilities should the partner undertake?",
    selectionMode: "multiple",
    decisionSignals: ["partner_responsibilities", "role_expectations"],
  },
  {
    id: "q13_target_markets_sought",
    section: "looking_for",
    title: "Support Provided by Your Company",
    decisionContext: "Company states what support it will provide to the selected partner.",
    userPromptGuide: "What support will your company provide to the selected partner?",
    selectionMode: "multiple",
    decisionSignals: ["partner_support", "contribution_offered"],
  },
  {
    id: "q14_timeline_readiness",
    section: "looking_for",
    title: "Preferred Commercial Relationship",
    decisionContext: "Company states the commercial model it is prepared to offer.",
    userPromptGuide: "What commercial model are you prepared to offer?",
    selectionMode: "multiple",
    decisionSignals: ["commercial_model", "engagement_structure"],
  },
  {
    id: Q15_PRIORITY_MARKET_ID,
    section: "looking_for",
    title: "Priority Collaboration Market",
    decisionContext: "Company states its highest-priority region and specific target countries for this opportunity.",
    userPromptGuide: "Which market is currently the highest priority for this opportunity?",
    selectionMode: "single",
    decisionSignals: ["priority_market", "target_geography"],
    followUp: STANDARD_FOLLOW_UP,
    answerGroupDefinitions: Q15_ANSWER_GROUP_DEFINITIONS,
  },
] as const;

const DEFINITION_BY_ID = new Map(
  INTENT_QUESTION_DEFINITIONS.map((def) => [def.id, def] as const),
);

export function getIntentQuestionDefinition(id: string): IntentQuestionDefinition | undefined {
  return DEFINITION_BY_ID.get(id);
}

export function getSectionLabel(section: IntentQuestionSection): string {
  return SECTION_LABELS[section];
}

export function isFollowUpAllowed(id: string): boolean {
  return (FOLLOW_UP_QUESTION_IDS as readonly string[]).includes(id);
}

export function isSingleSelectQuestion(id: string): boolean {
  return (SINGLE_SELECT_QUESTION_IDS as readonly string[]).includes(id);
}

/** Registry mode wins over a stored snapshot so existing questionnaires follow definition changes. */
export function resolveIntentSelectionMode(
  id: string,
  fallback?: IntentQuestionSelectionMode | null,
): IntentQuestionSelectionMode {
  return getIntentQuestionDefinition(id)?.selectionMode ?? fallback ?? "multiple";
}

export function hasAnswerGroups(id: string): boolean {
  return (ANSWER_GROUP_QUESTION_IDS as readonly string[]).includes(id);
}

export function getAnswerGroupDefinitions(id: string): readonly IntentAnswerGroupDefinition[] {
  return getIntentQuestionDefinition(id)?.answerGroupDefinitions ?? [];
}

export function getMaxSelections(id: string): number | undefined {
  return getIntentQuestionDefinition(id)?.maxSelections;
}

export function setsEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((item) => setB.has(item));
}

export const BANNED_INTENT_OPTION_TAGS = new Set([
  "good",
  "bad",
  "yes",
  "no",
  "option_1",
  "option_2",
  "option_3",
  "option_4",
  "other",
]);

export const SNAKE_CASE_TAG_REGEX = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

export const INTENT_QUESTION_OPTION_COUNT = 4;

/** Region-band tags AI should use on Q15 options for catalog filtering. */
export const Q15_REGION_BAND_TAGS = [
  "region_europe_uk",
  "region_india_south_asia",
  "region_middle_east_africa",
  "region_southeast_asia_apac",
] as const;

export const Q15_REGION_BAND_TO_CATALOG_REGION_IDS: Record<
  (typeof Q15_REGION_BAND_TAGS)[number],
  readonly string[]
> = {
  region_europe_uk: ["northern-europe", "western-europe", "eastern-europe", "southern-europe"],
  region_india_south_asia: ["indian-subcontinent"],
  region_middle_east_africa: [
    "middle-east-gcc",
    "middle-east-levant",
    "middle-east-north-africa",
    "latin-america-africa",
  ],
  region_southeast_asia_apac: [
    "asean-mainland",
    "asean-maritime",
    "east-asia",
    "greater-china",
    "oceania",
    "north-america",
  ],
};

export function resolveQ15RegionIdsFromOptionTags(tags: readonly string[]): string[] {
  for (const tag of tags) {
    const band = tag as (typeof Q15_REGION_BAND_TAGS)[number];
    const regions = Q15_REGION_BAND_TO_CATALOG_REGION_IDS[band];
    if (regions) return [...regions];
  }
  return [];
}
